import { useState } from 'react'
import {
  Bot,
  CheckCircle2,
  AlertCircle,
  ShoppingBag,
  Store,
  Truck,
  User,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  MessageSquarePlus,
  ShoppingCart,
  Phone,
  MapPin,
  CreditCard,
  Calendar,
} from 'lucide-react'
import hotToast from 'react-hot-toast'
import type { ConversationSalesContext } from '@/services/conversationsApi'
import { parseBotSummaryHandoff } from './botSummaryParser'
import { normalizeToVertical } from '@/lib/verticalUI'

export interface BotSummaryHandoffMessageProps {
  message: {
    id: string
    timestamp?: Date | string | number
    createdAt?: Date | string | number
    content?: string
    text?: string
    handoffSummaryContent?: string
    handoffNote?: {
      id: string
      content: string
      createdAt: string
    }
  }
  salesContext?: ConversationSalesContext | null
  formatTime: (date: Date | string | number) => string
  onCreateOrder?: () => void
  onPrefillMessage?: (text: string) => void
  agentName?: string
  businessType?: string | null
}

export function BotSummaryHandoffMessage({
  message,
  salesContext,
  formatTime,
  onCreateOrder,
  onPrefillMessage,
  agentName = 'del equipo de ventas',
  businessType,
}: BotSummaryHandoffMessageProps) {
  const [copied, setCopied] = useState(false)
  const [showTechnicalLog, setShowTechnicalLog] = useState(false)

  const rawSummaryText =
    message.handoffSummaryContent ||
    message.handoffNote?.content ||
    message.content ||
    message.text ||
    ''

  const parsed = parseBotSummaryHandoff(rawSummaryText, salesContext)

  const timestamp = message.timestamp || message.createdAt || message.handoffNote?.createdAt
  const timeFormatted = timestamp ? formatTime(timestamp) : ''

  const vertical = normalizeToVertical(businessType)
  const isAppointmentVertical = ['salud', 'servicios', 'belleza_estetica'].includes(vertical)
  const isInmobiliaria = vertical === 'inmobiliaria'
  const isTurismo = vertical === 'turismo_posadas'

  const handleCopySummary = async () => {
    try {
      await navigator.clipboard.writeText(rawSummaryText || parsed.confirmationDescription)
      setCopied(true)
      hotToast.success('Resumen copiado al portapapeles')
      setTimeout(() => setCopied(false), 2000)
    } catch {
      hotToast.error('No se pudo copiar el resumen')
    }
  }

  const handleLoadContextualGreeting = () => {
    if (!onPrefillMessage) return

    const customerName = parsed.customerName || 'Cliente'
    const greetingName = customerName !== 'Cliente' ? `¡Hola ${customerName}!` : '¡Hola!'
    const primaryItemName = parsed.items[0]?.name || 'tu pedido'
    const formattedTotal = parsed.formattedTotal

    let text = ''
    if (parsed.isOrderConfirmed) {
      if (isAppointmentVertical) {
        text = `${greetingName} Te escribe ${agentName}. Recibí tu solicitud para ${primaryItemName}${
          formattedTotal ? ` (${formattedTotal})` : ''
        }. Con gusto coordinamos tu cita y disponibilidad de horario. ¿Deseas agendar para esta semana?`
      } else {
        const isPickup = parsed.fulfillmentMethod === 'pickup'
        const methodText = isPickup
          ? 'retiro en tienda'
          : parsed.fulfillmentAddress
            ? `entrega por delivery a ${parsed.fulfillmentAddress}`
            : 'entrega por delivery'

        text = `${greetingName} Te escribe ${agentName}. Ya tengo anotado tu pedido de ${primaryItemName}${
          formattedTotal ? ` (${formattedTotal})` : ''
        } para ${methodText}. ¿Deseas los datos para transferencia / Pago Móvil o prefieres pagar al momento?`
      }
    } else {
      if (isAppointmentVertical) {
        text = `${greetingName} Te escribe ${agentName}. Vi tu consulta sobre ${primaryItemName}. ¿Tienes alguna duda sobre los horarios o deseas agendar una cita? Con gusto te asisto.`
      } else if (isInmobiliaria) {
        text = `${greetingName} Te escribe ${agentName}. Vi tu interés en ${primaryItemName}. ¿Te gustaría coordinar una visita presencial o recibir la ficha técnica?`
      } else {
        text = `${greetingName} Te escribe ${agentName}. Noté tu consulta sobre ${primaryItemName}. ¿En qué te puedo asesorar para ayudarte a concretar tu pedido?`
      }
    }

    onPrefillMessage(text)
  }

  return (
    <div
      role="region"
      aria-label="Resumen de escalación del Agente IA"
      className="mx-auto my-4 w-full max-w-3xl overflow-hidden rounded-2xl border-2 border-purple-300/80 bg-gradient-to-br from-purple-50/95 via-white to-indigo-50/70 p-4 sm:p-5 shadow-sm backdrop-blur-xs transition-all"
    >
      {/* 1. Header de Nota Interna (Destacado vs Mensaje al Cliente) */}
      <div className="flex flex-wrap items-center justify-between gap-2.5 border-b border-purple-200/70 pb-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-lg bg-purple-700 px-2.5 py-1 text-xs font-bold text-white shadow-2xs">
            <Bot className="h-4 w-4" />
            <span>RESUMEN DEL AGENTE IA</span>
          </span>

          <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-100/80 px-2.5 py-0.5 text-[11px] font-semibold text-purple-900">
            <span>🔒 Mensaje interno · NO enviado al cliente</span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          {timeFormatted && (
            <span className="text-xs font-semibold text-slate-500 tabular-nums">
              {timeFormatted}
            </span>
          )}

          <button
            type="button"
            onClick={handleCopySummary}
            className="inline-flex items-center gap-1 rounded-lg border border-purple-200 bg-white px-2 py-1 text-xs font-semibold text-purple-800 shadow-2xs hover:bg-purple-50 active:scale-95 transition-all"
            title="Copiar resumen al portapapeles"
          >
            {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
            <span>{copied ? 'Copiado' : 'Copiar'}</span>
          </button>
        </div>
      </div>

      {/* 2. Bloque Principal: Destacar Si el Pedido Está o No Confirmado */}
      <div className="mt-3">
        {parsed.isOrderConfirmed ? (
          <div className="rounded-xl border-2 border-emerald-400 bg-emerald-500/10 p-3 sm:p-3.5 text-emerald-950 shadow-2xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-emerald-600 text-white shadow-2xs">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs sm:text-sm font-black uppercase tracking-wide text-emerald-900">
                      {parsed.confirmationBadgeText}
                    </span>
                    <span className="rounded-full bg-emerald-700 px-2 py-0.5 text-[10px] font-bold text-white shadow-2xs">
                      Listo para Cobro y Despacho
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] sm:text-xs text-emerald-900/90 font-medium">
                    {parsed.confirmationDescription}
                  </p>
                </div>
              </div>

              {parsed.formattedTotal && (
                <div className="rounded-lg bg-emerald-700 px-3 py-1 text-white shadow-2xs text-right">
                  <span className="block text-[9px] uppercase font-bold text-emerald-200">Total a Cobrar</span>
                  <span className="text-sm sm:text-base font-black tracking-tight">{parsed.formattedTotal}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div className="rounded-xl border-2 border-amber-400 bg-amber-500/10 p-3 sm:p-3.5 text-amber-950 shadow-2xs">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-amber-500 text-white shadow-2xs">
                  <AlertCircle className="h-4 w-4" />
                </div>
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs sm:text-sm font-black uppercase tracking-wide text-amber-900">
                      {parsed.confirmationBadgeText}
                    </span>
                    <span className="rounded-full bg-amber-600 px-2 py-0.5 text-[10px] font-bold text-white shadow-2xs">
                      Atención y Asesoría Requerida
                    </span>
                  </div>
                  <p className="mt-0.5 text-[11px] sm:text-xs text-amber-900/90 font-medium">
                    {parsed.confirmationDescription}
                  </p>
                </div>
              </div>

              {parsed.formattedTotal && (
                <div className="rounded-lg bg-amber-600 px-3 py-1 text-white shadow-2xs text-right">
                  <span className="block text-[9px] uppercase font-bold text-amber-100">Presupuesto / Valor</span>
                  <span className="text-sm sm:text-base font-black tracking-tight">{parsed.formattedTotal}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* 3. Grid de Datos Estructurados: Cliente, Productos, Modalidad */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-xs text-slate-700">
        {/* Columna Cliente */}
        <div className="rounded-xl border border-purple-200/70 bg-white/90 p-2.5 shadow-2xs">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-purple-900/70 mb-1">
            <User className="h-3 w-3 text-purple-700" />
            <span>Cliente</span>
          </div>
          <p className="font-bold text-slate-900 truncate">
            {parsed.customerName || 'Cliente en consulta'}
          </p>
          {parsed.customerPhone && (
            <p className="flex items-center gap-1 text-[11px] text-slate-500 font-mono mt-0.5">
              <Phone className="h-2.5 w-2.5 text-slate-400" />
              <span>{parsed.customerPhone}</span>
            </p>
          )}
        </div>

        {/* Columna Productos */}
        <div className="rounded-xl border border-purple-200/70 bg-white/90 p-2.5 shadow-2xs">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-purple-900/70 mb-1">
            <ShoppingBag className="h-3 w-3 text-purple-700" />
            <span>
              {isAppointmentVertical
                ? 'Servicio / Cita'
                : isInmobiliaria
                  ? 'Inmueble'
                  : isTurismo
                    ? 'Hospedaje'
                    : parsed.isOrderConfirmed
                      ? 'Productos Confirmados'
                      : 'Productos de Interés'}
            </span>
          </div>
          {parsed.items.length > 0 ? (
            <div className="space-y-0.5 max-h-16 overflow-y-auto">
              {parsed.items.map((it, idx) => (
                <p key={idx} className="font-semibold text-slate-900 truncate" title={it.name}>
                  {it.quantity}x {it.name}
                  {it.price != null && (
                    <span className="text-[11px] font-normal text-slate-500 ml-1">
                      (${it.price.toFixed(2)})
                    </span>
                  )}
                </p>
              ))}
            </div>
          ) : (
            <p className="text-[11px] italic text-slate-500">Consulta general de catálogo</p>
          )}
        </div>

        {/* Columna Modalidad de Entrega */}
        <div className="rounded-xl border border-purple-200/70 bg-white/90 p-2.5 shadow-2xs">
          <div className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-purple-900/70 mb-1">
            {isAppointmentVertical ? (
              <Calendar className="h-3 w-3 text-purple-700" />
            ) : parsed.fulfillmentMethod === 'pickup' ? (
              <Store className="h-3 w-3 text-purple-700" />
            ) : (
              <Truck className="h-3 w-3 text-purple-700" />
            )}
            <span>Modalidad</span>
          </div>
          <p className="font-semibold text-slate-900 truncate">
            {isAppointmentVertical
              ? 'Atención en sede / consultorio'
              : parsed.fulfillmentMethod === 'pickup'
                ? 'Retiro en tienda'
                : parsed.fulfillmentMethod === 'delivery'
                  ? 'Delivery a domicilio'
                  : 'Por coordinar con el cliente'}
          </p>
          {parsed.fulfillmentAddress && (
            <p className="flex items-center gap-1 text-[11px] text-slate-600 line-clamp-1 mt-0.5" title={parsed.fulfillmentAddress}>
              <MapPin className="h-2.5 w-2.5 text-slate-400 shrink-0" />
              <span>{parsed.fulfillmentAddress}</span>
            </p>
          )}
          {parsed.paymentMethod && (
            <p className="flex items-center gap-1 text-[10px] text-emerald-800 font-semibold mt-0.5">
              <CreditCard className="h-2.5 w-2.5 text-emerald-600" />
              <span>Pago: {parsed.paymentMethod}</span>
            </p>
          )}
        </div>
      </div>

      {/* 4. Motivo de Escalación y Último Mensaje del Cliente (si existe) */}
      {(parsed.escalationReason || parsed.lastCustomerMessage) && (
        <div className="mt-2.5 rounded-xl border border-purple-200/60 bg-white/80 p-2.5 text-xs text-slate-700">
          {parsed.escalationReason && (
            <div className="flex items-start gap-1.5">
              <span className="font-bold text-purple-950 shrink-0">Motivo de escalación:</span>
              <span className="text-slate-800 font-medium">
                {parsed.escalationReason.label}
                {parsed.escalationReason.detail ? ` — ${parsed.escalationReason.detail}` : ''}
              </span>
            </div>
          )}

          {parsed.lastCustomerMessage && (
            <div className="mt-1 flex items-start gap-1.5 text-[11px] text-slate-600 italic border-l-2 border-purple-300 pl-2">
              <span>Último mensaje del cliente: &ldquo;{parsed.lastCustomerMessage}&rdquo;</span>
            </div>
          )}
        </div>
      )}

      {/* 5. Acción Requerida / Recomendada */}
      {parsed.actionRequired && (
        <div className="mt-2.5 flex items-start gap-2 rounded-xl bg-purple-100/60 p-2.5 text-xs text-purple-950 border border-purple-200/60">
          <span className="font-bold shrink-0">🎯 Acción sugerida:</span>
          <span className="font-medium text-purple-900 leading-snug">{parsed.actionRequired}</span>
        </div>
      )}

      {/* 6. Barra de Acciones de 1 Clic */}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2 pt-2.5 border-t border-purple-200/70">
        <button
          type="button"
          onClick={() => setShowTechnicalLog((prev) => !prev)}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800 transition-colors"
        >
          <span>{showTechnicalLog ? 'Ocultar bitácora técnica' : 'Ver bitácora técnica completa'}</span>
          {showTechnicalLog ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </button>

        <div className="flex flex-wrap items-center gap-2">
          {/* Botón: Cargar saludo */}
          {onPrefillMessage && (
            <button
              type="button"
              onClick={handleLoadContextualGreeting}
              className="inline-flex items-center gap-1.5 rounded-lg border border-purple-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-purple-900 shadow-2xs hover:bg-purple-50 active:scale-95 transition-all"
              title="Cargar respuesta sugerida contextual en el campo de texto"
            >
              <MessageSquarePlus className="h-3.5 w-3.5 text-purple-700" />
              <span>
                {parsed.isOrderConfirmed
                  ? 'Cargar saludo de cobro'
                  : 'Cargar saludo de asesoría'}
              </span>
            </button>
          )}

          {/* Botón: Crear o Ver Orden */}
          {onCreateOrder && (
            <button
              type="button"
              onClick={onCreateOrder}
              className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold text-white shadow-xs active:scale-95 transition-all ${
                parsed.isOrderConfirmed
                  ? 'bg-emerald-700 hover:bg-emerald-800'
                  : 'bg-purple-700 hover:bg-purple-800'
              }`}
              title={parsed.isOrderConfirmed ? 'Ver o gestionar orden registrada' : 'Crear orden comercial'}
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              <span>
                {parsed.isOrderConfirmed
                  ? `Ver / Gestionar Orden ${parsed.formattedTotal ? `(${parsed.formattedTotal})` : ''}`
                  : `Crear Orden ${parsed.formattedTotal ? `(${parsed.formattedTotal})` : ''}`}
              </span>
            </button>
          )}
        </div>
      </div>

      {/* 7. Bitácora Técnica Expandible */}
      {showTechnicalLog && rawSummaryText && (
        <div className="mt-2.5 rounded-lg bg-slate-900 p-3 text-[11px] text-slate-200 font-mono whitespace-pre-wrap max-h-52 overflow-y-auto border border-slate-800">
          {rawSummaryText}
        </div>
      )}
    </div>
  )
}
